(function (global) {
  'use strict';
  const rates = { SA: 95, BH: 90, KW: 90, QA: 100, OM: 70 };
  function summarize(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart');
    const lines = cart.items.filter(item => item.requires_shipping !== false && Number(item.quantity) > 0);
    const missing = [];
    let grams = 0;
    for (const item of lines) {
      const weight = Number(item.grams);
      const quantity = Number(item.quantity);
      if (item.grams == null || !Number.isFinite(weight) || weight <= 0 || !Number.isSafeInteger(quantity)) {
        missing.push(item);
      } else {
        grams += weight * quantity;
      }
    }
    if (!Number.isFinite(grams)) throw new Error('Invalid weight');
    return { state: !lines.length ? 'empty' : missing.length ? 'missing' : 'ready', grams, missing, lines };
  }
  function estimate(country, grams, rounding) {
    if (!Object.hasOwn(rates, country) || !Number.isFinite(grams) || grams <= 0) return { state: 'invalid' };
    if (grams > 1000 && grams % 1000 !== 0 && rounding !== 'ceil') return { state: 'rounding_pending' };
    const billableKg = Math.max(1, Math.ceil(grams / 1000));
    return { state: 'ready', billableKg, amount: rates[country] + (billableKg - 1) * 25 };
  }
  function manualEstimate(country, value) {
    const weight = String(value).trim();
    const kg = Number(weight);
    if (!weight || !Number.isSafeInteger(kg) || kg < 1) return { state: 'invalid' };
    return estimate(country, kg * 1000, 'pending');
  }
  const api = { summarize, estimate, manualEstimate };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  global.BumpinoShippingWeight = api;
  if (!global.document) return;
  const controllers = new Map();
  function init(root) {
    if (controllers.has(root)) return;
    const configNode = root.querySelector('[data-shipping-config]');
    if (!configNode) return;
    const config = JSON.parse(configNode.textContent);
    const form = root.querySelector('[data-shipping-calculator]');
    const country = form.querySelector('[data-shipping-country]');
    const mode = form.querySelector('[data-shipping-mode]');
    const manual = form.querySelector('[data-shipping-manual]');
    const weightInput = form.querySelector('[data-shipping-weight]');
    const result = form.querySelector('[data-shipping-result]');
    const weightText = form.querySelector('[data-shipping-cart-weight]');
    const status = form.querySelector('[data-shipping-status]');
    const missingList = form.querySelector('[data-shipping-missing]');
    const refresh = form.querySelector('[data-shipping-refresh]');
    const t = config.messages;
    const formatter = new Intl.NumberFormat(config.locale, { maximumFractionDigits: 3 });
    let summary = null;
    let error = false;
    let loading = false;
    let requestNumber = 0;
    let aborter = null;
    function render() {
      manual.hidden = mode.value !== 'manual';
      missingList.replaceChildren();
      weightText.textContent = summary ? formatter.format(summary.grams / 1000) + ' ' + t.kg : '—';
      if (summary && summary.state === 'missing') {
        for (const item of summary.missing) {
          const li = document.createElement('li');
          li.textContent = (item.product_title || item.title || t.item) + (item.variant_title ? ' — ' + item.variant_title : '') + ' × ' + item.quantity;
          missingList.append(li);
        }
      }
      result.textContent = '—';
      status.dataset.state = 'notice';
      let quote;
      if (mode.value === 'manual') {
        quote = manualEstimate(country.value, weightInput.value);
        status.textContent = quote.state === 'ready' ? t.manual_note : t.invalid;
      } else if (loading) {
        status.textContent = t.loading;
        return;
      } else if (error || !summary) {
        status.textContent = t.error;
        return;
      } else if (summary.state === 'empty') {
        status.textContent = t.empty;
        return;
      } else if (summary.state === 'missing') {
        status.textContent = t.missing;
        return;
      } else {
        quote = estimate(country.value, summary.grams, config.rounding);
        status.textContent = quote.state === 'rounding_pending' ? t.rounding_pending : t.cart_note;
      }
      if (quote.state === 'ready') {
        result.textContent = 'AED ' + formatter.format(quote.amount);
        status.dataset.state = 'ready';
      }
    }
    async function refreshCart() {
      const number = ++requestNumber;
      if (aborter) aborter.abort();
      aborter = new AbortController();
      loading = true;
      error = false;
      summary = null;
      refresh.disabled = true;
      render();
      try {
        const response = await fetch(config.cart_url, { credentials: 'same-origin', cache: 'no-store', signal: aborter.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart request failed');
        const cart = await response.json();
        if (number !== requestNumber) return;
        summary = summarize(cart);
      } catch (err) {
        if (number !== requestNumber || err.name === 'AbortError') return;
        error = true;
      } finally {
        if (number === requestNumber) {
          loading = false;
          refresh.disabled = false;
          render();
        }
      }
    }
    country.addEventListener('change', render);
    mode.addEventListener('change', () => { render(); if (mode.value === 'cart') refreshCart(); });
    weightInput.addEventListener('input', render);
    refresh.addEventListener('click', refreshCart);
    controllers.set(root, { refresh: refreshCart, stop: () => { requestNumber++; if (aborter) aborter.abort(); } });
    refreshCart();
  }
  function boot() { document.querySelectorAll('[data-bumpino-shipping-page]').forEach(init); }
  function refreshAll() {
    for (const [root, controller] of controllers) {
      if (!root.isConnected) { controller.stop(); controllers.delete(root); }
      else controller.refresh();
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', refreshAll);
  document.addEventListener('cart:updated', refreshAll);
  document.addEventListener('cart:change', refreshAll);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshAll(); });
  global.addEventListener('pageshow', refreshAll);
  global.addEventListener('focus', refreshAll);
})(typeof window !== 'undefined' ? window : globalThis);
